import { SmartActionsInputProps } from '@bitte-ai/types';
import { ArrowUpRight } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

interface WidgetChatInputProps extends SmartActionsInputProps {
  autoFocus?: boolean;
}

export const WidgetChatInput = ({
  input,
  isLoading,
  handleChange,
  handleSubmit,
  borderColor,
  textColor,
  backgroundColor,
  placeholderText,
  autoFocus = false,
}: WidgetChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track the last input we received from parent to detect external changes
  const lastExternalInput = useRef(input);

  useEffect(() => {
    if (!autoFocus || !textareaRef.current || !containerRef.current) return;

    // Create intersection observer to detect when widget becomes visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.scrollIntoView({ block: 'nearest' });
              }
            }, 100);
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '10px',
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [autoFocus]);

  // Only update textarea value if input changed externally (not from our own typing)
  useEffect(() => {
    if (textareaRef.current && input !== lastExternalInput.current) {
      // This is an external change (like clearing the input), so update the textarea
      const textarea = textareaRef.current;
      const currentCursor = textarea.selectionStart;

      textarea.value = input;
      lastExternalInput.current = input;

      // If the cursor position is still valid, restore it
      if (currentCursor <= input.length) {
        textarea.setSelectionRange(currentCursor, currentCursor);
      } else {
        // Position cursor at end if current position is beyond new text length
        textarea.setSelectionRange(input.length, input.length);
      }
    }
  }, [input]);

  // Fallback: Focus on any click in the widget area
  const handleContainerClick = () => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Update our tracking reference
    lastExternalInput.current = e.target.value;
    // Notify parent - this won't cause a re-render that affects our cursor
    handleChange(e);
  };

  return (
    <div ref={containerRef} onClick={handleContainerClick}>
      <form
        className="bitte-relative bitte-mb-0 bitte-flex bitte-flex-col bitte-w-full bitte-items-center bitte-justify-center bitte-gap-2 bitte-bg-mb-gray-150"
        style={{ color: textColor }}
        onSubmit={handleSubmit}
      >
        <div className="bitte-w-full">
          <Textarea
            ref={textareaRef}
            placeholder={placeholderText || 'Type your message...'}
            style={{
              background: backgroundColor,
              borderColor: borderColor,
            }}
            className="bitte-h-[42px] bitte-w-full bitte-resize-none bitte-min-h-0 textarea-chat"
            onChange={handleTextareaChange}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
              }
            }}
            value={input}
          />
        </div>

        <Button
          type="submit"
          disabled={!lastExternalInput.current || isLoading}
          className="bitte-h-[42px] bitte-w-full bitte-p-0 disabled:bitte-opacity-20 bitte-text-black bitte-bg-white"
        >
          <span>Send</span>
        </Button>
        <a
          href="https://bitte.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="bitte-text-[#6E6E6E] hover:bitte-text-white bitte-flex bitte-items-center bitte-gap-1"
        >
          <span className="bitte-text-[10px] bitte-font-semibold">
            Powered by Bitte
          </span>
          <ArrowUpRight className="bitte-w-2 bitte-h-2" />
        </a>
      </form>
    </div>
  );
};
